<template>
  <div>
    <b-card-code title="Modifier un colis">
      <b-card-text class="mt-1">
        <span>Veuillez saisir les informations logistiques du colis à modifier. Tous les champs marqués d’un * sont obligatoires.</span>
      </b-card-text>
      <!-- form -->
      <validation-observer ref="simpleRules">

        <b-form>
          <b-row>
            <b-col md="4">
              <b-form-group
                label="Nom du destinataire *"
                label-for="nom"
                description="Nom complet du destinataire du colis."
              >
                <validation-provider
                  #default="{ errors }"
                  name="nom"
                  rules="required"
                >
                  <b-form-input
                    v-model="order.name"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Nom du destinataire"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="4">
              <b-form-group
                label="Gouvernorat de destination *"
                label-for="gouvernorat"
                description="Sélectionnez le gouvernorat de destination du colis."
              >
                <validation-provider
                  #default="{ errors }"
                  name="gouvernorat"
                  rules="required"
                >
                  <v-select
                    v-model="order.governorate"
                    :clearable="false"
                    placeholder="Choisissez un gouvernorat de destination"
                    label="name"
                    :options="governorates"
                    @input="getDelegations()"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="4">
              <b-form-group
                label="Délégation de destination *"
                label-for="delegation"
                description="Sélectionnez la délégation de destination."
              >
                <validation-provider
                  #default="{ errors }"
                  name="delegation"
                >
                  <v-select
                    v-model="order.delegation"
                    :clearable="false"
                    placeholder="Choisissez une délégation de destination"
                    label="name"
                    :options="delegations"
                    @input="getLocalities()"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="4">
              <b-form-group
                label="Localité de destination"
                label-for="localité"
                description="Sélectionnez la localité de destination."
              >
                <validation-provider
                  #default="{ errors }"
                  name="localité"
                >
                  <v-select
                    v-model="order.locality"
                    :clearable="false"
                    placeholder="Choisissez une localité de destination"
                    label="name"
                    :options="localities"
                    :disabled="!order.governorate || !order.delegation"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="4">
              <b-form-group
                label="Adresse complète de livraison *"
                label-for="adresse"
                description="Adresse complète pour la livraison du colis."
              >
                <validation-provider
                  #default="{ errors }"
                  name="adresse"
                  rules="required"
                >
                  <b-form-input
                    v-model="order.address"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Adresse complète de livraison"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                label="Téléphone du destinataire *"
                label-for="Téléphone"
                description="Numéro de téléphone principal du destinataire (8 chiffres)."
              >
                <validation-provider
                  #default="{ errors }"
                  name="Téléphone"
                  rules="required|integer|length:8"
                >
                  <b-form-input
                    v-model="order.phone1"
                    type="tel"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Téléphone principal du destinataire"
                    :formatter="formatPhone"
                    maxlength="8"
                    minlength="8"
                    autocomplete="off"
                    @keypress="if (!/\d/.test($event.key)) $event.preventDefault()"
                    @input="order.phone1 = order.phone1.replace(/[^\d]/g, '').slice(0,8)"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                label="Téléphone secondaire"
                label-for="Téléphone2"
                description="Numéro de téléphone secondaire du destinataire (optionnel)."
              >
                <validation-provider
                  #default="{ errors }"
                  name="Telephone2"
                  rules="integer|length:8"
                >
                  <b-form-input
                    v-model="order.phone2"
                    type="tel"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Téléphone secondaire du destinataire"
                    :formatter="formatPhone"
                    maxlength="8"
                    minlength="8"
                    autocomplete="off"
                    @keypress="if (!/\d/.test($event.key)) $event.preventDefault()"
                    @input="order.phone2 = order.phone2.replace(/[^\d]/g, '').slice(0,8)"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col
              md="4"
            >
              <b-form-group
                label="Montant total TTC *"
                label-for="montant ttc"
                description="Montant total à payer, toutes taxes comprises."
              >
                <validation-provider
                  #default="{ errors }"
                  name="montant ttc"
                  rules="required"
                >
                  <b-form-input
                    v-model="order.price_ttc"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Montant total TTC"
                    type="number"
                    step="0.01"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col
              v-if="role === 'admin'"
              md="4"
            >
              <b-form-group
                label="Frais de livraison (DT) *"
                label-for="Prix de livraison"
                description="Frais de livraison appliqués à ce colis."
              >
                <validation-provider
                  #default="{ errors }"
                  name="Prix de livraison"
                  rules="required"
                >
                  <b-form-input
                    v-model="order.price_delivery"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Frais de livraison (DT)"
                    type="number"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col
              v-if="user.weight_price === true"
              md="4"
            >
              <b-form-group
                label="Poids volumétrique du colis *"
                label-for="Poids volumétrique de colis"
                description="Sélectionnez le poids volumétrique du colis pour le calcul logistique."
              >
                <validation-provider
                  #default="{ errors }"
                  name="Poids de colis"
                  rules="required"
                >
                  <v-select
                    v-model="order.weight"
                    :clearable="false"
                    placeholder="Poids volumétrique du colis"
                    label="weight"
                    :options="user.weights"
                    @input="fetch_price"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col
              md="4"
            >
              <b-form-group
                label="Mode de paiement du destinataire *"
                label-for="type"
                description="Sélectionnez le mode de paiement utilisé par le destinataire."
              >
                <validation-provider
                  #default="{ errors }"
                  name="type"
                  rules="required"
                >
                  <v-select
                    v-model="order.payment_method"
                    :clearable="false"
                    placeholder="Mode de paiement du destinataire"
                    label="value"
                    :options="types"
                    :reduce="type => type.key"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col
              md="4"
              class="demo-inline-spacing"
            >
              <b-form-checkbox
                v-model="order.is_an_exchange"
                name="check-button"
                switch
                inline
              >
                Colis à échanger
              </b-form-checkbox>
              <b-form-checkbox
                v-model="order.is_fragile"
                name="check-button"
                switch
                inline
              >
                Colis fragile
              </b-form-checkbox>
              <b-form-checkbox
                v-model="order.is_discharge"
                name="check-button"
                switch
                inline
              >
                Décharge (remise sans signature)
              </b-form-checkbox>
            </b-col>
          </b-row>
        </b-form>

        <b-card-code title="Produits">
          <b-card-text>
            <span>Veuillez saisir les informations logistiques des produits à livrer.</span>
          </b-card-text>
          <!-- form -->
          <validation-observer ref="simpleRules">
            <div>
              <div>
                <b-form
                  ref="form"
                  :style="{ height: trHeight }"
                  class="repeater-form"
                  @submit.prevent="repeateAgain"
                >
                  <!-- Row Loop -->
                  <b-row
                    v-for="(item, index) in items"
                    :id="item.id"
                    :key="item.id"
                    ref="row"
                  >

                    <!-- produit -->
                    <b-col md="6">
                      <b-form-group
                        label="Désignation du produit"
                        label-for="produit"
                        description="Nom ou désignation du produit à livrer."
                      >
                        <validation-provider
                          #default="{ errors }"
                          name="Désignation du produit"
                          rules="required"
                        >
                          <b-form-input
                            v-model="item.product"
                            placeholder="Désignation du produit"
                            :state="errors.length > 0 ? false : null"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <!-- Quantity -->
                    <b-col md="3">
                      <b-form-group
                        label="Quantité"
                        label-for="quantity"
                        description="Quantité du produit à livrer."
                      >
                        <b-form-input
                          id="quantity"
                          v-model="item.quantity"
                          type="number"
                          placeholder="Quantité"
                        />
                      </b-form-group>
                    </b-col>

                    <!-- Remove Button -->
                    <b-col
                      lg="2"
                      md="6"
                      class="mb-50"
                    >
                      <b-button
                        v-ripple.400="'rgba(234, 84, 85, 0.15)'"
                        variant="outline-danger"
                        class="mt-0 mt-md-2"
                        @click="removeItem(index)"
                      >
                        <feather-icon
                          icon="XIcon"
                          class="mr-25"
                        />
                        <span>Supprimer</span>
                      </b-button>
                    </b-col>
                    <b-col cols="12">
                      <hr>
                    </b-col>
                  </b-row>
                </b-form>
              </div>

              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="mb-2"
                @click="repeateAgain"
              >
                <feather-icon
                  icon="PlusIcon"
                  class="mr-25"
                />
                <span>Nouveau produit</span>
              </b-button>

              <b-row>

                <b-col md="12">
                  <b-form-group
                    label="Commentaire (optionnel)"
                    label-for="Commentaire"
                    description="Ajoutez un commentaire ou une instruction spéciale pour la livraison."
                  >
                    <b-form-textarea
                      id="textarea-rows"
                      v-model="order.note"
                      placeholder="Commentaire ou instruction spéciale"
                      rows="1"
                    />
                  </b-form-group>
                </b-col>

              </b-row>
              <b-row>
                <b-col cols="12">
                  <span id="edit-order-btn-wrapper">
                    <b-button
                      variant="primary"
                      type="submit"
                      class="mb-1 mb-sm-0 mr-0 mr-sm-1 mt-3"
                      :class="{ 'disabled-cursor': isLoading || !isOrderValid || items.some(item => !item.product) || order.address === '' || !order.delegation }"
                      :disabled="isLoading || !isOrderValid || items.some(item => !item.product) || order.address === '' || !order.delegation "
                      @click.prevent="updateOrder"
                    >
                      Mettre à jour le colis
                    </b-button>
                    <b-tooltip
                      v-if="!isOrderValid || items.some(item => !item.product) || order.address === '' || !order.delegation"
                      target="edit-order-btn-wrapper"
                      triggers="hover focus"
                      placement="top"
                    >
                      <template v-if="order.name === ''">
                        Saisissez le nom du destinataire pour activer la mise à jour.
                      </template>
                      <template v-else-if="order.phone1 === ''">
                        Saisissez le téléphone du destinataire pour activer la mise à jour.
                      </template>
                      <template v-else-if="order.price_ttc === ''">
                        Saisissez le montant total TTC pour activer la mise à jour.
                      </template>
                      <template v-else-if="order.governorate === ''">
                        Sélectionnez le gouvernorat de destination pour activer la mise à jour.
                      </template>
                      <template v-else-if="order.address === ''">
                        Saisissez l'adresse de livraison pour activer la mise à jour.
                      </template>
                      <template v-else-if="!order.delegation">
                        Le champ délégation est obligatoire pour activer la mise à jour.
                      </template>
                      <template v-else-if="items.some(item => !item.product)">
                        Veuillez saisir au moins un produit pour activer la mise à jour.
                      </template>
                      <template v-else>
                        Remplissez tous les champs obligatoires pour activer la mise à jour.
                      </template>
                    </b-tooltip>
                  </span>
                </b-col>
              </b-row>

            </div>
          </validation-observer>
        </b-card-code>
      </validation-observer>
    </b-card-code>

    <!-- Client Statistics Section -->
    <div>
      <h3>Client Purchase Statistics</h3>
      <input
        v-model="clientPhone"
        placeholder="Client Phone"
        @blur="fetchClientStats"
      >
      <input
        v-model="clientName"
        placeholder="Client Name"
        @blur="fetchClientStats"
      >

      <div v-if="clientStats">
        <p>Total Orders: {{ clientStats.total_orders }}</p>
        <p>Total Spent: {{ clientStats.total_spent }}</p>
        <p>Last Order Date: {{ clientStats.last_order_date }}</p>
        <ul>
          <li
            v-for="clientOrder in clientStats.orders"
            :key="clientOrder.id"
          >
            {{ clientOrder.created_at }} - {{ clientOrder.price_ttc }} - {{ clientOrder.status }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import BCardCode from '@core/components/b-card-code'
import vSelect from 'vue-select'
import { heightTransition } from '@core/mixins/ui/transition'
import Ripple from 'vue-ripple-directive'
import { ValidationProvider, ValidationObserver } from 'vee-validate'

/* import vSelect from 'vue-select' */
import {
  BFormInput,
  BFormGroup,
  BForm,
  BRow,
  BCol,
  BButton,
  BFormTextarea,
  BCardText /*  */,
  BFormCheckbox,
} from 'bootstrap-vue'
import { required } from '@validations'
import storeAuth from '@/store/store'

export default {
  name: 'OrderEdit',
  components: {
    BCardCode,
    ValidationProvider,
    ValidationObserver,
    BCardText,
    /* BFormTextarea, */
    BFormInput,
    BFormGroup,
    BForm,
    BRow,
    BCol,
    BButton,
    BFormTextarea,
    vSelect,
    BFormCheckbox,
  },
  directives: {
    Ripple,
  },
  mixins: [heightTransition],
  data() {
    return {
      items: [
        {
          type: '',
          product: '',
          quantity: 1,
        },
      ],
      itemsOptions: [],
      nextTodoId: 1,
      type: '',
      types: [
        {
          key: 0,
          value: 'Espèce',
        },
        {
          key: 1,
          value: 'par chèque',
        },
        {
          key: 2,
          value: 'par traite',
        },
      ],
      governorates: [],
      governorate: {},
      delegations: [],
      localities: [],
      order: {
      },
      required,
      user: {
        weight_price: false,
      },
      role: null,
      isLoading: false,
      clientPhone: '',
      clientName: '',
      clientStats: null,
    }
  },
  computed: {
    isOrderValid() {
      return (
        this.order.name !== ''
        && this.order.phone1 !== ''
        && this.order.price_ttc !== ''
        && this.order.governorate !== ''
      )
    },
  },
  mounted() {
    this.initTrHeight()
  },
  created() {
    this.role = storeAuth.state.user.role
    // If supplier, redirect back and do not allow access
    if (this.role === 'supplier') {
      this.$router.go(-1)
      return
    }
    this.items.shift()
    this.getOrder()
    this.getGovernorates()
  },
  destroyed() {
    window.removeEventListener('resize', this.initTrHeight)
  },
  methods: {
    async fetch_price() {
      const result = this.user.prices.find(element => element.weight.id === this.order.weight.id)
      this.order.price_delivery = result.delivery_price
      this.order.price_return = result.return_price
    },
    async getOrder() {
      this.isLoading = true
      const { data } = await axios.get(`/api/orders/edit/${this.$route.params.id}`)
      this.order = data
      this.user = this.order.supplier
      this.governorate = this.order.governorate

      this.getDelegations()
      if (this.order != null) {
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < this.order.products.length; i++) {
          const qte = this.order.products[i].quantity
          const { product } = this.order.products[i]
          this.repeate(qte, product)
        }
      }
      this.isLoading = false
    },
    async getGovernorates() {
      const { data } = await axios.get('/api/governorates/')
      this.governorates = data
    },
    async getDelegations() {
      const { data } = await axios.get('/api/delegations/', { params: { governorate: this.order.governorate.id } })
      this.delegations = data
      this.getLocalities()
    },
    async getLocalities() {
      const { data } = await axios.get('/api/localities/', { params: { delegation: this.order.delegation.id } })
      this.localities = data
    },
    repeate(qte, product) {
      this.items.push({
        product,
        quantity: qte,
      })

      this.$nextTick(() => {
        this.trAddHeight(this.$refs.row[0].offsetHeight)
      })
    },
    repeateAgain() {
      this.items.push({
        product: '',
        quantity: 1,
      })

      this.$nextTick(() => {
        this.trAddHeight(this.$refs.row[0].offsetHeight)
      })
    },
    removeItem(index) {
      this.items.splice(index, 1)
      this.trTrimHeight(this.$refs.row[0].offsetHeight)
    },
    initTrHeight() {
      this.trSetHeight(null)
      this.$nextTick(() => {
        this.trSetHeight(this.$refs.form.scrollHeight)
      })
    },

    async updateOrder() {
      const { simpleRules } = this.$refs
      const isValid = await simpleRules.validate()
      if (isValid) {
        let delegation = null
        let localityId = null
        let weight = null
        if (this.order.delegation != null) {
          delegation = this.order.delegation.id
        }
        if (this.order.locality != null) {
          localityId = this.order.locality.id
        }
        if (this.order.weight != null) {
          weight = this.order.weight.id
        }
        try {
          await axios.put(
            `/api/orders/update/${this.$route.params.id}/`, {
              name: this.order.name,
              address: this.order.address,
              phone1: this.order.phone1,
              phone2: this.order.phone2,
              note: this.order.note,
              price_ttc: this.order.price_ttc,
              price_delivery: this.order.price_delivery,
              price_return: this.order.price_return,
              type: this.order.type,
              governorate: this.order.governorate.id,
              delegation,
              locality: localityId,
              products: this.items,
              is_an_exchange: this.order.is_an_exchange,
              is_fragile: this.order.is_fragile,
              is_discharge: this.order.is_discharge,
              payment_method: this.order.payment_method,
              weight,
            },
          )
          this.$router.push({ name: 'order-show', params: { id: this.$route.params.id } })
          setTimeout(() => {
            this.showToast('success', 'top-center', 'Colis modifié avec succés')
          }, 1000)
        } catch (err) {
          setTimeout(() => {
            this.showToast('danger', 'top-center', err.toString())
          }, 1000)
        }
      } else {
        setTimeout(() => {
          this.showToast('danger', 'top-center', 'champ obligatoire ! vérifier votre donnée !')
        }, 1000)
      }
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
    formatPhone(e) {
      return String(e).substring(0, 8)
    },
    async fetchClientStats() {
      if (!this.clientPhone && !this.clientName) return
      const params = new URLSearchParams()
      if (this.clientPhone) params.append('phone', this.clientPhone)
      if (this.clientName) params.append('name', this.clientName)
      const resp = await fetch(`/api/orders/client-purchase-stats/?${params.toString()}`, {
        headers: { Authorization: `Bearer ${this.$store.state.token}` },
      })
      if (resp.ok) {
        this.clientStats = await resp.json()
      } else {
        this.clientStats = null
      }
    },
  },
  beforeRouteEnter(to, from, next) {
    // Update breadcrumb with order ID when entering the route
    next(vm => {
      if (to.params.id) {
        // Create new breadcrumb array to avoid mutating route meta directly
        const newBreadcrumb = [
          {
            text: 'Colis',
            to: { name: 'order-list', params: { level: 0 } },
          },
          {
            text: `Modifier Colis #${to.params.id}`,
            active: true,
          },
        ]

        // Use Vue.set or Object.assign to properly update the meta
        vm.$set(vm.$route.meta, 'breadcrumb', newBreadcrumb)
      }
    })
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>

<style lang="scss" scoped>
.repeater-form {
  overflow: hidden;
  transition: 0.35s height;
}
.disabled-cursor {
  cursor: not-allowed;
  opacity: 0.8;
}
</style>
